import React, { useState, useMemo } from 'react';
import { useMetricData } from '../hooks/UseMetricData';
import { TwTabs as Tabs, Text, Button } from "@virkesborsen/ui-component-library";
import { formatNumber } from '../../utils';
import { fixValues } from './helpers/helpers';

export default function MetricAreaWidget() {
    const { totalArea, totalOverviewClassification } = useMetricData();
    const [secondaryTabSelected, setSecondaryTabSelected] = useState('ha')

    const tableData = [
        { color: "#3F8888", data: "Skogsmark", totalHa: Math.round(totalOverviewClassification?.forest_land ?? 0) },
        { color: "#D84E46", data: "Övrig mark", totalHa: Math.round(totalOverviewClassification?.other ?? 0) },
        { color: "#7C6ED9", data: "Vatten", totalHa: Math.round(totalOverviewClassification?.water ?? 0) },
        { color: "#D5EE64", data: "Åkermark", totalHa: Math.round(totalOverviewClassification?.field ?? 0) }
    ];
    const totalAreas = tableData.reduce((sum, item) => sum + parseInt(item?.totalHa ?? 0), 0);

   const noData = tableData.every(item => item.totalHa === 0);

   const tableCalculations = () => {
        if (!Array.isArray(tableData) || tableData.length === 0) {
            return [];
        }
        const fixTableValues = fixValues(tableData,"totalHa", totalArea)
        const percentageValues= fixTableValues.map((value)=>{
            const percentageWithDecimal = totalArea > 0 ? (value.totalHa / totalArea * 100) : 0
            return{
                ...value,
                percentage:percentageWithDecimal
            }
        })

        const fixedPercentageData = fixValues(percentageValues,"percentage", 100)

        return fixedPercentageData.map(item => {
            let totalValue, unit;
            if (secondaryTabSelected === 'ha') {
                totalValue = formatNumber(item?.totalHa ?? 0);
                unit = "ha";
            } else if (secondaryTabSelected === "percentage") {
                totalValue = item?.percentage?.toString();
                unit = "%";
            }
            return {
                ...item,
                color: item.color,
                percentage: `${item?.percentage}%`,
                unit: unit,
                total: totalValue,
                data: item.data
            };
        })
    }

    const finalTableData = useMemo(() => tableCalculations(), [tableData, secondaryTabSelected, totalAreas])

    // Helpers
    const secondaryTabs = [{ label: 'ha', value: 'ha' }, { label: '%', value: 'percentage' }];

    if(noData) {
        return (
            <div>
                <div className="py-2 flex justify-between">
                    <div className='px-4 flex items-baseline justify-between w-full'>
                        <div className='pe-4 w-full flex'>
                            <Text variant="text-xl/leading-none/medium">{totalArea || totalArea === 0 ? totalArea.toLocaleString('sv-SE') : ""}
                                <span><Text variant="text-xs/leading-none/normal"> hektar <span className="text-[#737373]">(ha)</span></Text></span></Text>
                        </div>
                    </div>
                </div>

                <div className='py-4 px-4 flex items-center justify-between'>
                    <Text variant="text-sm/leading-none/black">
                    Gick inte att hämta data för en eller flera fastigheter.
                    </Text>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Header */}
            <div className="py-2 flex justify-between">
                <div className='px-4 flex items-baseline justify-between w-full'>
                    <div className='pe-4 w-full flex'>
                        <Text variant="text-xl/leading-none/medium">{totalArea || totalArea === 0 ? totalArea.toLocaleString('sv-SE') : ""}
                            <span><Text variant="text-xs/leading-none/normal"> hektar <span className="text-[#737373]">(ha)</span></Text></span></Text>
                    </div>
                    <div className="rounded-xs h-2 flex overflow-hidden w-full bg-[#F5F5F5]">
                        {finalTableData.filter(item => item.totalHa > 0).map((data, index) => {
                            return (
                                <div
                                    key={index}
                                    style={{ width: data.percentage, backgroundColor: data.color }}
                                    title={`${data.data}: ${data.percentage}`}
                                >
                                </div>
                            );
                        })}
                    </div>

                </div>
            </div>

            {/*Tabs and Button section */}
            <div className="py-2 flex justify-between flex-wrap px-2 gap-2">
                <Button
                    onClick={() => { }}
                    variant="outline"
                    size='xsmall'
                    tw
                >Arealer</Button>
                <div className="bg-cloud-200 rounded-lg-alt items-center justify-center">
                    <Tabs
                        defaultValue="ha"
                        tabs={secondaryTabs}
                        onClick={setSecondaryTabSelected}
                    />
                </div>
            </div>

            {/* Table section */}
            <div className=" overflow-y-auto h-[280px]">
                <table className="table-auto w-full">
                    <tbody>
                        {finalTableData.map((data, index) => (
                            <tr key={index} className="flex justify-between w-full py-3 border-cloud-400 border-b px-4">
                                <td className="flex items-center gap-1">
                                    <div className={`rounded-full h-2 w-2 gap-2`} style={{ backgroundColor: data.color }}></div>
                                    <span><Text variant="text-sm/leading-none/normal" className="ps-1-5"> {data.data}</Text></span>
                                </td>
                                <td><Text variant="text-sm/leading-none/normal">{data.total}<span className="text-[#737373] ps-2">{data.unit}</span></Text></td>
                            </tr>
                        ))}
                        <tr className="py-3 flex justify-between w-full border-cloud-400 border-b bg-cloud-200 px-4">
                            <td><Text variant="text-sm/leading-none/normal">Total areal</Text></td>
                            <td><Text variant="text-sm/leading-none/medium">{totalArea} <span className="ps-2">ha</span></Text></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}